import type { AppClient } from '../client';
import { HttpAdminClient } from './adminClient';
import { HttpAnnotationClient } from './annotationsClient';
import { HttpAuthClient } from './authClient';
import { HttpCsvClient } from './csvClient';
import { HttpFeedsClient } from './feedsClient';
import { HttpFilesClient } from './filesClient';
import { HttpFulfillmentClient } from './fulfillmentClient';
import { HttpHealthClient } from './healthClient';
import { HttpIdentityClient } from './identityClient';
import { HttpIntakeClient } from './intakeClient';
import { HttpMetadataClient } from './metadataClient';
import { HttpPendingClient } from './pendingClient';
import { HttpRecordClient } from './recordsClient';
import { HttpReportClient } from './reportsClient';
import { createTransport } from './transport';
import { HttpViewClient } from './viewsClient';
import { HttpWebhookClient } from './webhooksClient';
import { HttpWorkflowClient } from './workflowsClient';

/**
 * THE real client: the three interfaces every screen talks to, over HTTP.
 *
 * This is what main.tsx constructs instead of `FixtureClient`, and it is the entire
 * integration step - no screen, hook or component knows which one it got.
 *
 * The record client depends on the metadata client rather than on a second transport,
 * because a record page needs the field ids of `created_at` and friends and the metadata
 * client already holds the graph. One cache, one request.
 */
export interface AppHttpClientOptions {
  /** Empty in the app: same origin behind Caddy, and behind Vite's proxy in development. */
  readonly baseUrl?: string;
  /** Called on any 401. The session layer redirects to the login screen once. */
  readonly onUnauthenticated?: (() => void) | undefined;
}

export class AppHttpClient implements AppClient {
  readonly metadata: HttpMetadataClient;
  readonly records: HttpRecordClient;
  readonly auth: HttpAuthClient;
  readonly admin: HttpAdminClient;
  readonly pending: HttpPendingClient;
  readonly identity: HttpIdentityClient;
  readonly webhooks: HttpWebhookClient;
  readonly annotations: HttpAnnotationClient;
  readonly reports: HttpReportClient;
  readonly views: HttpViewClient;
  readonly workflows: HttpWorkflowClient;
  readonly csv: HttpCsvClient;
  readonly files: HttpFilesClient;
  readonly intake: HttpIntakeClient;
  readonly health: HttpHealthClient;
  readonly feeds: HttpFeedsClient;
  readonly fulfillment: HttpFulfillmentClient;

  constructor(options: AppHttpClientOptions = {}) {
    const transport = createTransport({
      baseUrl: options.baseUrl ?? '',
      onUnauthenticated: options.onUnauthenticated,
    });
    this.metadata = new HttpMetadataClient(transport);
    this.records = new HttpRecordClient(transport, this.metadata);
    this.auth = new HttpAuthClient(transport);
    this.admin = new HttpAdminClient(transport);
    this.pending = new HttpPendingClient(transport);
    this.identity = new HttpIdentityClient(transport);
    this.webhooks = new HttpWebhookClient(transport);
    this.annotations = new HttpAnnotationClient(transport);
    this.reports = new HttpReportClient(transport);
    this.views = new HttpViewClient(transport);
    this.workflows = new HttpWorkflowClient(transport);
    this.csv = new HttpCsvClient(transport);
    this.files = new HttpFilesClient(transport);
    this.intake = new HttpIntakeClient(transport);
    this.health = new HttpHealthClient(transport);
    this.feeds = new HttpFeedsClient(transport);
    this.fulfillment = new HttpFulfillmentClient(transport);
  }
}

export { ApiError, NetworkError } from './transport';
